import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw, Home } from "lucide-react";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  compact?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary caught an unhandled error]:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/dashboard";
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.compact) {
        return (
          <div className="p-4 rounded-lg bg-card border border-border shadow-card flex items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-warning">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>
                <strong className="text-foreground">{this.props.fallbackTitle || "Notice"}:</strong>{" "}
                <span className="text-muted-foreground">{this.state.error?.message || "Unavailable"}</span>
              </span>
            </div>
            <Button size="sm" variant="ghost" onClick={this.handleReset} className="h-7 text-xs gap-1">
              <RefreshCw className="w-3 h-3" /> Retry
            </Button>
          </div>
        );
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-card border border-border/80 rounded-xl p-6 shadow-sm text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-rose-500/10 text-rose-600 flex items-center justify-center mx-auto">
              <AlertCircle className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">
                {this.props.fallbackTitle || "Something went wrong"}
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                An unexpected error occurred while rendering this view.
              </p>
              {this.state.error?.message && (
                <div className="mt-3 p-2.5 bg-muted/60 rounded text-left font-mono text-[11px] text-rose-600 dark:text-rose-400 border border-border/50 max-h-24 overflow-y-auto">
                  {this.state.error.message}
                </div>
              )}
            </div>
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={this.handleReset} className="text-xs gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" /> Reload View
              </Button>
              <Button size="sm" onClick={this.handleGoHome} className="text-xs gap-1.5">
                <Home className="w-3.5 h-3.5" /> Dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
